import React, { useState } from 'react';
import DataTable from 'react-data-table-component';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchPurityConfigurations,
  deletePurityConfiguration,
  setPurityConfigurationEdit,
  clearPurityConfigurationEdit,
} from '../../../../redux/actions/masters/metalPurity/purityConfiguration/purityConfigurationAction';
import { showConfirm, showInfo } from '../../../../utills/sweetAlert';
import SearchIcon from '@mui/icons-material/Search';

const cardStyle = {
  background: 'transparent',
  border: 'none',
  boxShadow: 'none',
  padding: 0,
  minWidth: 0,
  marginBottom: 0,
  fontFamily: 'Poppins, sans-serif',
};
const headerStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: 18,
};
const titleStyle = {
  fontWeight: 700,
  fontSize: 18,
  color: '#111',
  letterSpacing: 0.7,
};
const newBtnStyle = {
  background: 'linear-gradient(135deg, #00C853 0%, #43A047 100%)',
  color: '#fff',
  border: 'none',
  borderRadius: 8,
  padding: '10px 26px',
  fontWeight: 700,
  fontSize: 16,
  cursor: 'pointer',
  marginRight: 12,
  letterSpacing: 0.5,
  transition: 'all 0.3s ease',
  boxShadow: '0 2px 8px rgba(0, 200, 83, 0.3)',
};
const searchContainer = {
  position: 'relative',
  minWidth: 200,
  marginLeft: 8,
};
const searchInput = {
  width: 200,
  padding: '10px 36px 10px 38px',
  border: '1.5px solid #C8E6C9',
  borderRadius: 8,
  fontSize: 16,
  color: '#1B5E20',
  background: '#fff',
  fontFamily: 'inherit',
  outline: 'none',
  transition: 'border 0.3s',
};
const searchIconStyle = {
  position: 'absolute',
  left: 12,
  top: '50%',
  transform: 'translateY(-50%)',
  color: '#00C853',
  fontSize: 20,
  pointerEvents: 'none',
};
const actionStyle = {
  cursor: 'pointer',
  marginRight: 10,
  background: 'none',
  border: 'none',
  borderRadius: 6,
  padding: 4,
  color: '#757575',
  transition: 'all 0.3s ease',
};

const PurityConfigurationTable = ({ onNew, purityConfigurations, metalTypes }) => {
  const dispatch = useDispatch();
  const { loading, error } = useSelector((state) => state.purityConfiguration || {});
  const [search, setSearch] = useState('');
  const [hoveredAction, setHoveredAction] = useState({});

  const getMetalTypeName = (code) => {
    const type = metalTypes.find(m => m.metal_type_code === code);
    return type ? type.metal_type_name : '';
  };

  const handleEdit = async (purityConfiguration) => {
    await showInfo('Edit Purity Configuration', `Editing purity configuration: ${purityConfiguration.metal_purity_code}`);
    dispatch(setPurityConfigurationEdit(purityConfiguration));
  };

  const handleDelete = async (id, code) => {
    const confirmed = await showConfirm('Trash Purity Configuration', `Are you sure you want to trash purity configuration: ${code}?`, 'Trash', 'Cancel');
    if (confirmed) {
      dispatch(deletePurityConfiguration(id));
    }
  };

  const handleNew = () => {
    dispatch(clearPurityConfigurationEdit());
    if (typeof onNew === 'function') onNew();
  };

  const filteredPurityConfigurations = purityConfigurations.filter(pc =>
    pc.metal_purity_code && pc.metal_purity_code.toLowerCase().includes(search.toLowerCase()) && pc.trash === 0
  );

  const columns = [
    {
      name: 'Metal Purity Code',
      selector: row => row.metal_purity_code,
      sortable: true,
      width:'160px'
    },
    {
      name: 'Description',
      selector: row => row.metal_purity_description,
      sortable: true,
      width:'160px'

    },
    {
      name: 'Fineness',
      selector: row => row.fineness,
      sortable: true,
    },
    {
      name: 'Metal Type Code',
      selector: row => row.metal_type_code,
      sortable: true,
      width:'160px'

    },
    {
      name: 'Status',
      selector: row => row.status,
      sortable: true,
      width: '110px',
      cell: row => (
        <span style={{ color: row.status === 'Active' ? '#00C853' : '#d32f2f', fontWeight: 600 }}>{row.status}</span>
      ),
    },
    {
      name: 'Actions',
      cell: row => (
        <>
          <button
            style={actionStyle}
            onClick={() => handleEdit(row)}
            title="Edit"
            onMouseEnter={() => setHoveredAction({ id: row.purity_configuration_id, type: 'edit' })}
            onMouseLeave={() => setHoveredAction({})}
          >
            <EditIcon fontSize="small" />
            {hoveredAction.id === row.purity_configuration_id && hoveredAction.type === 'edit' && (
              <span style={{ marginLeft: 4, color: '#1B5E20', fontWeight: 600 }}>Edit</span>
            )}
          </button>
          <button
            style={actionStyle}
            onClick={() => handleDelete(row.purity_configuration_id, row.metal_purity_code)}
            title="Trash"
            onMouseEnter={() => setHoveredAction({ id: row.purity_configuration_id, type: 'delete' })}
            onMouseLeave={() => setHoveredAction({})}
          >
            <DeleteOutlineIcon fontSize="small" />
            {hoveredAction.id === row.purity_configuration_id && hoveredAction.type === 'delete' && (
              <span style={{ marginLeft: 4, color: '#d32f2f', fontWeight: 600 }}>Trash</span>
            )}
          </button>
        </>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      width: '140px',
    },
  ];

  // Helper to check if error is a handled code error
  const isHandledCodeError = error && (
    error.includes('409') ||
    error.toLowerCase().includes('already exists') ||
    error.toLowerCase().includes('cannot be reused')
  );

  return (
    <div style={cardStyle}>
      <style>{`
        .purity-configuration-table ::-webkit-scrollbar {
          height: 6px;
          background: #f2f2f2;
        }
        .purity-configuration-table ::-webkit-scrollbar-thumb {
          background: #d3d3d3;
          border-radius: 4px;
        }
        .purity-configuration-table {
          scrollbar-color: #d3d3d3 #f2f2f2;
          scrollbar-width: thin;
        }
      `}</style>
      <div style={headerStyle}>
        <span style={titleStyle}>Purity Configuration List</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button style={newBtnStyle} onClick={handleNew}>+ New</button>
          <div style={searchContainer}>
            <input
              style={searchInput}
              type="text"
              placeholder="Search..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            <SearchIcon style={{ ...searchIconStyle, color: '#00C853' }} />
          </div>
        </div>
      </div>
      <DataTable
        columns={columns}
        data={filteredPurityConfigurations}
        pagination
        highlightOnHover
        striped
        progressPending={loading}
        noDataComponent={<div style={{ color: '#888', fontSize: 16 }}>No Purity Configurations found.</div>}
        customStyles={{
          headRow: { style: { background: '#E8F5E9', fontWeight: 700 } },
          rows: { style: { minHeight: 48 } },
        }}
        className="purity-configuration-table"
      />
      {error && !isHandledCodeError && <div style={{ color: 'red', fontSize: 15, marginTop: 8 }}>{error}</div>}
    </div>
  );
};

export default PurityConfigurationTable; 